import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import config from 'config'
import { useAuth0Authentication } from './useAuth0Authentication'

 interface CompanyInfo { 
    id: string, 
    name: string 
}

export type Merchant = CompanyInfo
type Organisation = CompanyInfo

export interface User {
    user_id: string,
    firstName: string,
    surname: string,
    name: string,
    email: string,
    lastLogin: string,
    organisation: Organisation,
    merchant?: Merchant,
    role: string,
    userType: string,
}

interface IGetUsersSearchResponse {
    pages: number,
    page: number,
    pageSize: number,
    noOfResults: number,
    results: User[]
}

export interface CreateUserRequest {
    firstName: string,
    surname: string,
    email: string,
    role: string,
    userType: string,
    merchant?: Merchant
}

export interface UpdateRoleRequest {
    userId: string, 
    role: string 
}

export interface IUseUsers {
    users: User[] | undefined,
    isLoading: boolean,
    error: Error | null,
    pages: number,
    page: number,
    pageSize: number,
    noOfResults: number,
    addUser: (user: CreateUserRequest) => Promise<void>,
    isAdding: boolean,
    addError: Error | null,
    updateUserRole: (request: UpdateRoleRequest) => Promise<void>,
    isUpdatingRole: boolean,
    updateRoleError: Error | null,
    deleteUser: (userId: string) => Promise<void>,
    isDeleting: boolean,
    deleteError: Error | null,
    refetchUsers: (page?: number) => void
}

const fetchUsers = async (accessToken: string, page?: number): Promise<IGetUsersSearchResponse> => {
    const pageParam: string = page !== undefined ? `?page=${page}` : ``
    
    const response = await fetch(
        `${config.apiUrl}/users${pageParam}`,
        {
            method: 'GET',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`)
    }

    const usersResponse: IGetUsersSearchResponse = await response.json()
    return usersResponse
}

// Separate API function for adding users
const addUserAPI = async (accessToken: string, user: CreateUserRequest): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/users`,
        {
            method: 'POST',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(user)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to add user: ${response.statusText}`)
    }
}

// Separate API function for updating user role
const updateUserRoleAPI = async (accessToken: string, userId: string, role: string): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/users/${encodeURI(userId)}`,
        {
            method: 'PATCH',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify({ role })
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to update user role: ${response.statusText}`)
    }
}

// Separate API function for deleting users
const deleteUserAPI = async (accessToken: string, userId: string): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/users/${encodeURI(userId)}`,
        {
            method: 'DELETE',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.statusText}`)
    }
}

export const useUsers = (page?: number): IUseUsers => {
    const { getAuth0AccessToken } = useAuth0Authentication()
    const queryClient = useQueryClient()

    const {
        data: usersResponse,
        isLoading,
        error,
        refetch
    } = useQuery<IGetUsersSearchResponse, Error>({
        queryKey: ['users', page],
        queryFn: async () => {
            const accessToken = await getAuth0AccessToken()
            return fetchUsers(accessToken, page)
        }
    })

    const {
        mutateAsync: addUser,
        isPending: isAdding,
        error: addError
    } = useMutation<void, Error, CreateUserRequest>({
        mutationFn: async (user: CreateUserRequest) => {
            const accessToken = await getAuth0AccessToken()
            const response = await addUserAPI(accessToken, user)
            return response
        },
        onSuccess: async () => {
            // Wait for server to process (adjust timing as needed)
            await new Promise(resolve => setTimeout(resolve, 1000))
            queryClient.invalidateQueries({ queryKey: ['users'] })
        }
    })

    const {
        mutateAsync: updateUserRole,
        isPending: isUpdatingRole,
        error: updateRoleError
    } = useMutation<void, Error, UpdateRoleRequest>({
        mutationFn: async (request: UpdateRoleRequest) => {
            const accessToken = await getAuth0AccessToken()
            const response = await updateUserRoleAPI(accessToken, request.userId, request.role)
            return response
        },
        onSuccess: async () => {
            // Wait for server to process (adjust timing as needed)
            await new Promise(resolve => setTimeout(resolve, 1000))
            queryClient.invalidateQueries({ queryKey: ['users'] })
        }
    })

    const {
        mutateAsync: deleteUser,
        isPending: isDeleting,
        error: deleteError
    } = useMutation<void, Error, string>({
        mutationFn: async (userId: string) => {
            const accessToken = await getAuth0AccessToken()
            const response = await deleteUserAPI(accessToken, userId)
            return response
        },
        onSuccess: async () => {
            // Wait for server to process (adjust timing as needed)
            await new Promise(resolve => setTimeout(resolve, 1000))
            queryClient.invalidateQueries({ queryKey: ['users'] })
        }
    })

    const refetchUsers = (newPage?: number) => {
        if (newPage !== undefined) {
            queryClient.refetchQueries({ queryKey: ['users', newPage] })
        } else {
            refetch()
        }
    }

    return {
        users: usersResponse?.results,
        isLoading,
        error: error || null,
        pages: usersResponse?.pages || 0,
        page: usersResponse?.page || 0,
        pageSize: usersResponse?.pageSize || 0,
        noOfResults: usersResponse?.noOfResults || 0,
        addUser,
        isAdding,
        addError: addError || null,
        updateUserRole,
        isUpdatingRole,
        updateRoleError: updateRoleError || null,
        deleteUser,
        isDeleting,
        deleteError: deleteError || null,
        refetchUsers
    }
} 